import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Col, Row } from "reactstrap";
import { Card, Tag, Switch, Input, Upload, message } from "antd";
import { EditOutlined, InfoCircleOutlined, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { WorkOrderFormData, GeneralCondition, ConditionAttachment } from "../types";
import { getPredefinedConditions } from "../mockData/workOrderData";
import Swal from "sweetalert2";
import { Button as AntdButton } from "antd";
const { TextArea } = Input;

const WorkOrderGeneralConditions: React.FC = (props: any) => {
  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<WorkOrderFormData>();

  const generalConditionsWatch = watch("generalConditions") || [];
  const [predefinedConditions, setPredefinedConditions] = useState<any[]>([]);
  const [editingCondition, setEditingCondition] = useState<string | null>(null);
  const [conditionContent, setConditionContent] = useState<string>("");

  useEffect(() => {
    fetchPredefinedConditions();
  }, []);

  // Debug effect to monitor generalConditions changes
  useEffect(() => {
    console.log("GeneralConditions Watch updated:", generalConditionsWatch);
  }, [generalConditionsWatch]);

  const fetchPredefinedConditions = async () => {
    try {
      const conditions = getPredefinedConditions();
      setPredefinedConditions(conditions);
    } catch (error) {
      console.log("Error fetching predefined conditions:", error);
    }
  };

  const validateForm = (data: any) => {
    // General conditions are optional - user can skip this step
    // But if they selected any conditions, those must have content or attachments

    if (data.generalConditions && data.generalConditions.length > 0) {
      // Check if all selected conditions have content or attachments
      for (const condition of data.generalConditions) {
        const hasContent = condition.conditionContent && condition.conditionContent.trim() !== "";
        const hasAttachments = condition.attachments && condition.attachments.length > 0;

        if (!hasContent && !hasAttachments) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `Please provide content or upload a document for condition: ${condition.conditionName}`,
          });
          return false;
        }
      }
    }

    return true;
  };

  const submitForm = async (data: WorkOrderFormData) => {
    console.log("General Conditions data:", data);
    if (!validateForm(data)) {
      console.log("Validation failed");
      return;
    }

    const payload = {
      ...data,
      step: "generalConditions",
    };

    console.log("General Conditions Payload:", payload);
    props.next(payload);
  };

  const handlePrevious = () => {
    if (props.prev) {
      props.prev();
    }
  };

  const handleToggleCondition = (conditionId: string) => {
    const currentConditions = getValues("generalConditions") || [];
    const existingIndex = currentConditions.findIndex(
      (c) => c.id === conditionId
    );

    if (existingIndex >= 0) {
      // Remove condition
      const updatedConditions = currentConditions.filter(
        (_, i) => i !== existingIndex
      );
      setValue("generalConditions", updatedConditions);
    } else {
      // Add condition
      const predefinedCondition = predefinedConditions.find(
        (c) => c.id === conditionId
      );
      if (predefinedCondition) {
        const newCondition: GeneralCondition = {
          id: predefinedCondition.id,
          conditionName: predefinedCondition.name,
          conditionContent: "", // Start with empty content
          isActive: true,
          order: predefinedCondition.order,
          attachments: [],
        };
        setValue("generalConditions", [...currentConditions, newCondition]);
      }
    }
  };

  const handleEditCondition = (conditionId: string) => {
    const currentConditions = getValues("generalConditions") || [];
    const condition = currentConditions.find((c) => c.id === conditionId);
    if (condition) {
      setEditingCondition(conditionId);
      setConditionContent(condition.conditionContent);
    }
  };

  const handleSaveConditionContent = () => {
    if (editingCondition) {
      const currentConditions = getValues("generalConditions") || [];
      const updatedConditions = currentConditions.map((c) =>
        c.id === editingCondition
          ? { ...c, conditionContent: conditionContent }
          : c
      );
      setValue("generalConditions", updatedConditions);
      setEditingCondition(null);
      setConditionContent("");
    }
  };

  const handleCancelEdit = () => {
    setEditingCondition(null);
    setConditionContent("");
  };

  const getSelectedCondition = (conditionId: string) => {
    return generalConditionsWatch.find((c) => c.id === conditionId);
  };

  const handleFileUpload = (conditionId: string, fileList: any[]) => {
    const currentConditions = getValues("generalConditions") || [];
    const updatedConditions = currentConditions.map((c) => {
      if (c.id === conditionId) {
        const attachments: ConditionAttachment[] = fileList.map((file, index) => ({
          id: `${conditionId}-${index}-${Date.now()}`,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadDate: new Date().toISOString(),
          url: file.url || file.thumbUrl,
        }));
        return { ...c, attachments };
      }
      return c;
    });
    setValue("generalConditions", updatedConditions);
  };

  const handleRemoveFile = (conditionId: string, fileId: string) => {
    const currentConditions = getValues("generalConditions") || [];
    const updatedConditions = currentConditions.map((c) => {
      if (c.id === conditionId) {
        const updatedAttachments = (c.attachments || []).filter((att) => att.id !== fileId);
        return { ...c, attachments: updatedAttachments };
      }
      return c;
    });
    setValue("generalConditions", updatedConditions);
  };

  const beforeUpload = (file: File) => {
    const isValidType = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type);
    if (!isValidType) {
      message.error('You can only upload PDF, JPG, PNG, DOC, DOCX files!');
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('File must be smaller than 10MB!');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <Card className="mb-4">
          {/* <p className="text-muted mb-4">
            Select and configure the applicable general conditions for this work order. 
            You can customize the content for each selected condition to match your specific requirements.
          </p> */}

          <div className="alert alert-info mb-4">
            <InfoCircleOutlined className="me-2" />
            <strong>Instructions:</strong> General conditions are optional. You can skip this step if not needed.
            If you want to include conditions, toggle them below. For each selected condition, you must either
            provide text content OR upload a document. You can also do both if needed.
          </div>

          <Row className="gy-3">
            {predefinedConditions.map((condition) => {
              const selectedCondition = getSelectedCondition(condition.id);
              const isSelected = !!selectedCondition;

              return (
                <Col lg="12" key={condition.id}>
                  <div
                    className={`p-3 border rounded ${
                      isSelected ? "border-primary bg-light" : "border-light"
                    }`}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center">
                        <Switch
                          checked={isSelected}
                          onChange={() => handleToggleCondition(condition.id)}
                          className="me-3"
                        />
                        <div>
                          <h6 className="mb-1">
                            {condition.order}. {condition.name}
                          </h6>
                          <Tag color={isSelected ? "success" : "default"}>
                            {isSelected ? "Selected" : "Available"}
                          </Tag>
                        </div>
                      </div>
                      {isSelected && (
                        <AntdButton
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditCondition(condition.id)}
                        >
                          Edit Content
                        </AntdButton>
                      )}
                    </div>

                    {/* Show content and file upload - editable if selected */}
                    {isSelected && editingCondition === condition.id ? (
                      <div className="mt-3">
                        <TextArea
                          value={conditionContent}
                          onChange={(e) => setConditionContent(e.target.value)}
                          rows={4}
                          placeholder="Enter condition content (optional if uploading document)..."
                        />
                        <div className="mt-2">
                          <AntdButton
                            type="primary"
                            size="small"
                            onClick={handleSaveConditionContent}
                            className="me-2"
                          >
                            Save
                          </AntdButton>
                          <AntdButton  size="small" className="me-2" onClick={handleCancelEdit}>
                            Cancel
                          </AntdButton>
                        </div>
                      </div>
                    ) : isSelected ? (
                      <div className="mt-3">
                        {/* Content Display */}
                        {selectedCondition?.conditionContent && (
                          <div className="p-2 bg-white border rounded mb-3">
                            <h6 className="small mb-1">Content:</h6>
                            <p className="mb-0 small">
                              {selectedCondition.conditionContent}
                            </p>
                          </div>
                        )}
                        
                        {/* File Upload Section */}
                        <div className="border rounded p-3 bg-light">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0 small">Documents</h6>
                            <Upload
                              beforeUpload={beforeUpload}
                              onChange={({ fileList }) => handleFileUpload(condition.id, fileList)}
                              fileList={selectedCondition?.attachments?.map((att) => ({
                                uid: att.id,
                                name: att.fileName,
                                status: 'done' as const,
                                url: att.url,
                              })) || []}
                              multiple
                              showUploadList={false}
                            >
                              <AntdButton size="small" icon={<UploadOutlined />}>
                                Upload Document
                              </AntdButton>
                            </Upload>
                          </div>
                          
                          {/* Uploaded Files List */}
                          {selectedCondition?.attachments && selectedCondition.attachments.length > 0 ? (
                            <div className="uploaded-files">
                              {selectedCondition.attachments.map((attachment) => (
                                <div key={attachment.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                  <div className="d-flex align-items-center">
                                    <span className="small me-2">📎</span>
                                    <div>
                                      <span className="small fw-medium">{attachment.fileName}</span>
                                      <br />
                                      <span className="text-muted" style={{fontSize: '11px'}}>
                                        {(attachment.fileSize / 1024 / 1024).toFixed(2)}MB
                                      </span>
                                    </div>
                                  </div>
                                  <AntdButton
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemoveFile(condition.id, attachment.id)}
                                    danger
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted small mb-0">No documents uploaded</p>
                          )}
                        </div>
                        
                        {/* Validation message */}
                        {!selectedCondition?.conditionContent && (!selectedCondition?.attachments || selectedCondition.attachments.length === 0) && (
                          <div className="alert alert-warning mt-2 small" role="alert">
                            <strong>Note:</strong> Please provide either text content or upload a document for this condition.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <p className="text-muted small mb-0">
                          Click the toggle above to select this condition and add content or documents.
                        </p>
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>

          {/* <div className="mt-4 p-3 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>Selected Conditions Summary</strong>
                <br />
                <small className="text-muted">
                  {generalConditionsWatch.length} out of {predefinedConditions.length} conditions selected
                </small>
              </div>
              <div>
                <Tag color="blue" className="mb-0">
                  {generalConditionsWatch.length} condition(s) active
                </Tag>
              </div>
            </div>

            {generalConditionsWatch.length > 0 && (
              <div className="mt-3">
                <strong className="small">Selected conditions:</strong>
                <div className="mt-1">
                  {generalConditionsWatch
                    .sort((a, b) => a.order - b.order)
                    .map((condition) => (
                      <Tag key={condition.id} color="processing" className="mb-1">
                        {condition.order}. {condition.conditionName}
                      </Tag>
                    ))}
                </div>
              </div>
            )}
          </div> */}

          {generalConditionsWatch.length === 0 && (
            <div className="alert alert-info mt-4">
              <strong>Note:</strong> No general conditions selected. You can proceed without conditions or
              select conditions above to include them in the work order.
            </div>
          )}
        </Card>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button
                color="primary"
                type="submit"
              >
                Next
              </Button>
            </li>
            <li>
              <Button type="button" onClick={handlePrevious}>
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </form>
    </>
  );
};

export default WorkOrderGeneralConditions;
